import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/axios';
import { useLocale } from '@/context/LocaleContext';
import { useSilkBalance } from '@/hooks/useSilkBalance';
import type { ServiceResponse } from '@/hooks/useTitleManager';
import { PurchaseConfirmModal } from '@/components/PurchaseConfirmModal';
import { TITLE_COLOR_PALETTE } from '@/data/titleColorPalette';
import { notifyFromResult, type TmNotifyType } from './titleManagerUtils';

type NameColor = {id:number; color_hex:string; isactive:boolean|number};
export function NameTab({onNotify}:{onNotify:(message:string,type?:TmNotifyType)=>void}) {
 const {t}=useLocale();
 const queryClient=useQueryClient();
 const [selected,setSelected]=useState<string|null>(null);
 const [confirm,setConfirm]=useState(false);
 const {data,isLoading,isError}=useQuery({queryKey:['titlemanager','name-colors'],queryFn:async()=>
  (await api.get<{colors:NameColor[];price:number}>('/api/v1/titlemanager/name-colors')).data});
 const {data:balance,isLoading:balanceLoading}=useSilkBalance(confirm);
 const mutation=useMutation({mutationFn:async(payload:{color:string}|{color_id:number})=>
  (await api.post<ServiceResponse>(`/api/v1/titlemanager/${'color' in payload?'buy':'apply'}-name-color`,payload)).data,
  onSuccess:async(result)=>{
   notifyFromResult(result,t,onNotify,'tm_error_buying_title');
   if(result.success){await queryClient.invalidateQueries({queryKey:['titlemanager','name-colors']});
    await queryClient.invalidateQueries({queryKey:['silk-balance']});setConfirm(false);}
  },onError:()=>onNotify(t('tm_name_error'),'danger')});
 const owned=data?.colors.some(row=>row.color_hex===selected);
 return <>
  <div className="titlemanager-content"><div className="titlemanager-grid">
   <div className="titlemanager-list"><table className="titlemanager-table"><thead><tr>
    <th>{t('tm_name_colors')}</th><th>{t('tm_actions_column')}</th>
   </tr></thead><tbody>
    {isLoading||isError||!data?.colors.length?<tr><td colSpan={2} className="tm-empty-cell">{t(isLoading?'loading_data':isError?'tm_name_error':'tm_name_empty')}</td></tr>:
    data.colors.map(row=><tr key={row.id} className={row.isactive?'tm-row-active':undefined}><td>
     <div className="title-item"><span className="tm-name-swatch" style={{backgroundColor:row.color_hex}}/><span className="title-text">{row.color_hex}</span></div>
    </td><td><div className="titlemanager-actions"><button type="button" className="titlemanager-action-btn" aria-label={`${t('tm_name_apply')} ${row.color_hex}`} title={t('tm_name_apply')}
     disabled={Boolean(row.isactive)||mutation.isPending} onClick={()=>mutation.mutate({color_id:row.id})}><i className="fas fa-check" aria-hidden/></button></div></td></tr>)}
   </tbody></table></div>
   <div className="titlemanager-editor">
    <div className="tm-editor-label">{t('tm_tab_name')}</div>
    <div className="titlemanager-input-group"><div className="tm-name-preview">
     {selected?<><span className="tm-name-swatch" style={{backgroundColor:selected}}/>{selected}</>:t('tm_name_select')}
    </div><button type="button" className="titlemanager-btn" disabled={!selected||!data||owned||mutation.isPending} onClick={()=>setConfirm(true)}>{t(owned?'tm_name_owned':'tm_buy')}</button></div>
    <div className="titlemanager-prices"><div className="price-item"><span>{t('tm_total_pay')}</span><span className="price-amount">{data?`${data.price} Silks`:'—'}</span></div></div>
    <p className="tm-name-help">{t('tm_name_reuse')}</p>
    <div className="titlemanager-color-palette"><div className="color-grid">{TITLE_COLOR_PALETTE.map(color=><button type="button" key={color.hex} title={color.label} aria-label={color.label} aria-pressed={selected===color.hex}
     className={`color-option${selected===color.hex?' selected':''}`} style={{backgroundColor:color.hex}} onClick={()=>setSelected(color.hex)}/>)}</div></div>
   </div>
  </div></div>
  <PurchaseConfirmModal className="silk-modal--aaa" open={confirm} headerTitle={t('tm_name_buy')} price={data?.price??999} balance={balance??null} balanceLoading={balanceLoading} confirming={mutation.isPending}
   onClose={()=>{if(!mutation.isPending)setConfirm(false);}} onConfirm={()=>{if(selected)mutation.mutate({color:selected});}}>
   <div className="tm-name-preview"><span className="tm-name-swatch" style={{backgroundColor:selected??'#FFFFFF'}}/>{selected}</div>
  </PurchaseConfirmModal>
 </>;
}
